import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Button, Typography } from "@mui/material";
import { tokens } from "../../../theme";
import { DateOnlyFormatter } from "../../../helpers/formatters";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../api/main/appStore";
import ModalDecisionContent from "../../shared/modal/ModalDecisionContent";
import { FinancierModel } from "../../../api/models/financier";

interface Props {
  data: FinancierModel[];
}

const columns = ["Name", "Email", "Phone", "Date", " "];

export default observer(function PendingFinanciersTable({ data }: Props) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const colors = tokens();
  const { commonStore, financierStore } = useStore();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleApproveFinancierClick = async (financier: FinancierModel) => {
    try {
      commonStore.setLoading(true);
      const bankDetail = await financierStore.getBankDetails(financier.id);

      if (bankDetail.length > 0) {
        const detail = bankDetail[0];
        commonStore.setModalContent(
          <ModalDecisionContent
            actionName={`approve a ${financier.name}'s application`}
            actionCallback={() =>
              financierStore.changeFinancierState({
                id: financier.id,
                isActive: true,
              })
            }
            futherDetail={
              <Box
                sx={{
                  color: colors.grey[300],
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    Account Name
                  </Typography>
                  <Typography>{detail.accountName}</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    Account Number
                  </Typography>
                  <Typography>{detail.accountNumber}</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    Bank
                  </Typography>
                  <Typography>{detail.bankName}</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    BVN
                  </Typography>
                  <Typography>{detail.bvn}</Typography>
                </Box>
              </Box>
            }
          />,
          ""
        );
      }
    } catch (error) {
      commonStore.handleAppError(error);
    } finally {
      commonStore.setLoading(false);
    }
  };

  const handleDeleteFinancierClick = (financier: FinancierModel) =>
    commonStore.setModalContent(
      <ModalDecisionContent
        actionName={`decline ${financier.name}'s application`}
        actionCallback={() =>
          financierStore.deleteFinancier(financier.id, false)
        }
      />,
      ""
    );

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-head": {
                  color: `${colors.grey[600]}`,
                  fontWeight: "bolder",
                },
              }}
            >
              {columns.map((column, index) => (
                <TableCell key={index} align="left" style={{ maxWidth: 100 }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.contactEmail}</TableCell>
                    <TableCell>{row.contactPhone}</TableCell>
                    <TableCell>{DateOnlyFormatter(row.dateModified)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="success"
                        onClick={() => handleApproveFinancierClick(row)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteFinancierClick(row)}
                      >
                        Decline
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
});
